<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import General from "@/services/General";
import ModalAddEditChangelog from "@/components/modals/dashboard/modalAddEditChangelog";

export default {
  components: { Layout, PageHeader, ModalAddEditChangelog },
  page: {
    title: "Manage Dashboard Changelog",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "Manage Dashboard Changelog",
      items: [
        {
          text: "Dashboard",
        },
        {
          text: "Manage Changelog",
          active: true,
        },
      ],
      changelogData: [],
      error: null,
      isBusy: false,
      totalRows: 0,
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'commit_hash', label: 'Commit Hash', sortable: false },
        { key: 'author_name', label: 'Author', sortable: true },
        { key: 'commit_message', label: 'Commit Message', sortable: true },
        { key: 'branch', label: 'Branch', sortable: false },
        { key: 'commit_timestamp', label: 'Commit Time', sortable: true },
        { key: 'status', label: 'Status', sortable: false, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'actions', label: 'Actions', sortable: false, thClass: 'text-center', tdClass: 'text-center' },
      ],
      perPage: 10,
      currentPage: 1,
      pageOptions: [10, 25, 50, 100],
      sortBy: 'commit_timestamp',
      sortDesc: true,
    };
  },
  async mounted() {
    await this.loadChangelog();
  },
  methods: {
    async loadChangelog() {
      this.isBusy = true;
      this.error = null;
      try {
        const response = await General.getDashboardGitChangelog();
        if (response.data && response.data.data) {
          this.changelogData = response.data.data;
          this.totalRows = this.changelogData.length;
        } else if (response.data && response.data.error) {
          this.error = response.data.error;
          this.changelogData = [];
          this.totalRows = 0;
        }
      } catch (err) {
        this.error = err.response?.data?.error || err.message || "Failed to load changelog";
        this.changelogData = [];
        console.error("Error loading dashboard git changelog:", err);
      } finally {
        this.isBusy = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateString;
      }
    },
    formatCommitHash(hash) {
      if (!hash) return "N/A";
      if (hash.length <= 12) return hash;
      const firstPart = hash.substring(0, 6);
      const lastPart = hash.substring(hash.length - 6);
      return `${firstPart}.......${lastPart}`;
    },
    transferToChangelog(item) {
      // Open modal in transfer mode with git changelog data
      this.$refs.modalAddEditChangelog.openModal(null, {
        commit_message: item.commit_message || '',
        commit_hash: item.commit_hash || ''
      });
    },
    onChangelogRefresh() {
      // This will be called when modal emits onRefresh
      // No need to reload git changelog, but you can add logic here if needed
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Error Message -->
            <div v-if="error" class="alert alert-warning mb-4" role="alert">
              <h6 class="alert-heading">
                <i class="uil uil-exclamation-triangle me-2"></i>
                Unable to load changelog
              </h6>
              <p class="mb-0">{{ error }}</p>
            </div>

            <!-- Table -->
            <div v-if="!error">
              <div class="row mt-4">
                <div class="col-sm-12 col-md-6">
                  <div id="tickets-table_length" class="dataTables_length">
                    <label class="d-inline-flex align-items-center">
                      Show&nbsp;
                      <b-form-select
                        v-model="perPage"
                        size="sm"
                        :options="pageOptions"
                      ></b-form-select>
                      &nbsp;entries
                    </label>
                  </div>
                </div>
              </div>
              <b-table
                :busy="isBusy"
                :items="changelogData"
                :fields="fields"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                striped
                hover
                responsive
                show-empty
                empty-text="No changelog entries available"
                class="mb-0"
              >
                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                  </div>
                </template>
                <template v-slot:cell(commit_hash)="data">
                  <code class="text-primary">{{ formatCommitHash(data.value) }}</code>
                </template>
                <template v-slot:cell(commit_timestamp)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template v-slot:cell(processed_at)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template v-slot:cell(commit_message)="data">
                  <span class="text-wrap">{{ data.value || 'N/A' }}</span>
                </template>
                <template v-slot:cell(status)="data">
                  <span v-if="data.item.is_transferred" class="badge bg-success">
                    <i class="uil uil-check-circle me-1"></i>Transferred
                  </span>
                  <span v-else class="badge bg-secondary">
                    <i class="uil uil-clock me-1"></i>Pending
                  </span>
                </template>
                <template v-slot:cell(actions)="data">
                  <a
                    v-if="!data.item.is_transferred"
                    href="javascript:void(0);"
                    class="px-2 text-primary"
                    title="Transfer to Dashboard Changelog"
                    @click="transferToChangelog(data.item)"
                  >
                    <i class="uil uil-arrow-right font-size-18"></i>
                  </a>
                  <span v-else class="text-muted" title="Already transferred">
                    <i class="uil uil-check font-size-18"></i>
                  </span>
                </template>
              </b-table>
              <div class="row">
                <div class="col">
                  <div class="dataTables_paginate paging_simple_numbers float-end">
                    <ul class="pagination pagination-rounded">
                      <b-pagination
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                      ></b-pagination>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Add/Edit/Transfer Changelog -->
    <ModalAddEditChangelog
      ref="modalAddEditChangelog"
      @onRefresh="onChangelogRefresh"
    />
  </Layout>
</template>